import { useCallback, useEffect, useState } from 'react'
import { useAuthFetch } from '../lib/useAuthFetch'
import type { EligibleAssignee } from '../lib/taskAssign'

const API = import.meta.env.VITE_API_URL ?? ''

// Fetch the active, location-authorized assignee roster for a task.
//   - locationId set   → members allowed at that location (server-scoped).
//   - locationId null  → every active member (used for the Action Queue name map).
// Returns [] (not an error) when team management is not configured — the picker
// then simply offers no teammates, and the caller can fall back to a plain note.
export function useEligibleAssignees(locationId: string | null | undefined) {
  const authFetch = useAuthFetch()
  const [assignees, setAssignees] = useState<EligibleAssignee[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    const qs = locationId ? `?location_id=${encodeURIComponent(locationId)}` : ''
    authFetch(`${API}/api/v1/annotations/eligible-assignees${qs}`)
      .then((r) => r.json())
      .then((d) => setAssignees(d?.data?.assignees ?? []))
      .catch(() => { setAssignees([]); setError("Couldn't load your team roster") })
      .finally(() => setLoading(false))
  }, [authFetch, locationId])

  useEffect(() => { load() }, [load])

  return { assignees, loading, error, reload: load }
}
